import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  FormGroup,
  Input,
  Alert,
} from 'reactstrap';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/img/MaeCoreDark.png';
import './Login.css';

//PARA TEMPORADA NAVIDEÑA
import { Snowfall } from 'react-snowfall';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiereEmpresa, setRequiereEmpresa] = useState(false);
  const [empresasDisponibles, setEmpresasDisponibles] = useState([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState('');
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, empresaSeleccionada || null);

      if (result.success) {
        // Verificar que el token esté guardado en sessionStorage
        const token = sessionStorage.getItem('token');
        const user = sessionStorage.getItem('user');

        if (token && user) {
          // Usar navigate con un pequeño delay para asegurar que el estado se actualice
          // El ProtectedRoute verificará sessionStorage directamente
          setTimeout(() => {
            navigate('/admin/dashboard', { replace: true });
          }, 100);
        } else {
          setError('Error: No se pudo guardar la sesión');
          setLoading(false);
        }
      } else {
        setError(result.message || 'Error al iniciar sesión');
        setLoading(false);
      }
    } catch (err) {
      if (err.StatusCode === 400 && (err.data?.RequiereEmpresa || err.data?.requiereEmpresa)) {
        setRequiereEmpresa(true);
        setEmpresasDisponibles(err.data?.Empresas || err.data?.empresas || []);
        setError(err.message || 'Existen múltiples cuentas. Seleccione su empresa para continuar');
        setLoading(false);
        return;
      }
      
      setError(err.message || 'Error al conectar con el servidor');
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Header con contacto y redes sociales */}
      <Snowfall color='#fff' />
      <header className="login-header">
        <div className="login-header-content">
          <div className="login-contact-info">
            <a href="mailto:contacto@maesoft.com.mx" className="login-email-link">
              contacto@maesoft.com.mx
            </a>
            <a href="mailto:care@maesoft.com.mx" className="login-email-link">
              care@maesoft.com.mx
            </a>
          </div>
          <div className="login-social-links">
            <a
              href="https://www.facebook.com/maesoft.oficial/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              title="Go to Facebook page"
            >
              <i className="fab fa-facebook-f"></i>
            </a>
            <a
              href="https://www.instagram.com/maesoft_oficial/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              title="Go to Instagram page"
            >
              <i className="fab fa-instagram"></i>
            </a>
            <a
              href="https://www.linkedin.com/company/maesoftoficial/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              title="Go to LinkedIn page"
            >
              <i className="fab fa-linkedin-in"></i>
            </a>
            <a
              href="https://www.threads.net/@maesoft_oficial"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              title="Go to Threads page"
            >
              <i className="fas fa-hashtag"></i>
            </a>
          </div>
        </div>
      </header>

      {/* Contenido principal */}
      <main className="login-main">
        {/* Logo */}
        <div className="login-logo-container">
          <img src={logo} alt="Mae Core" className="login-logo" />
        </div>

        {/* Formulario de login */}
        <div className="login-form-container">
          <div className="login-form-card">
            <h1 className="login-title">Iniciar Sesión</h1>
            <p className="login-subtitle">Sistema de Gestión Comercial Multisucursal</p>

            {error && (
              <Alert color="danger" className="login-alert">
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="login-form" autoComplete="off">
              <FormGroup className="login-form-group">
                <label htmlFor="email" className="login-label">
                  Correo Electrónico
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="correo@dominio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="login-input"
                  autoComplete="off"
                  data-form-type="other"
                />
              </FormGroup>

              <FormGroup className="login-form-group">
                <label htmlFor="password" className="login-label">
                  Contraseña
                </label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="login-input"
                  autoComplete="new-password"
                  data-form-type="other"
                />
              </FormGroup>

              {requiereEmpresa && empresasDisponibles.length > 0 && (
                <FormGroup className="login-form-group">
                  <label htmlFor="empresa" className="login-label">
                    Empresa (*)
                  </label>
                  <Input
                    type="select"
                    id="empresa"
                    value={empresaSeleccionada}
                    onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                    required
                    className="login-input"
                  >
                    <option value="" disabled>-- Seleccione su empresa --</option>
                    {empresasDisponibles.map((emp) => (
                      <option key={emp.EmpresaID || emp.empresaID} value={emp.EmpresaID || emp.empresaID}>
                        {emp.Nombre || emp.nombre}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="login-button"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="login-footer">
        <div className="login-footer-content">
          <div className="login-footer-links">
            <a href="https://maesoft.com.mx" className="login-footer-link">Maesoft</a>
            <a href="https://maesoft.com.mx/#acerca" className="login-footer-link">About</a>
            <a href="https://maesoft.com.mx/#contacto" className="login-footer-link">Contact Us</a>
          </div>
          <div className="login-footer-copyright">
            © 2025 made with <span className="login-footer-heart">❤</span> by{' '}
            <a href="https://maesoft.com.mx" className="login-footer-maesoft-link">
              Maesoft Solutions
            </a>{' '}
            for a better web.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Login;
